import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { initMetaPixel, trackPageView } from './utils/metaPixel';

// Components
import AccessibilityWidget from './components/common/AccessibilityWidget';
import CookieConsent from './components/common/CookieConsent';

// Layouts
import MainLayout from './components/layouts/MainLayout';
import DashboardLayout from './components/layouts/DashboardLayout';

// Public Pages
import HomePage from './pages/HomePage';
import CampaignsPage from './pages/CampaignsPage';
import CampaignDetailPage from './pages/CampaignDetailPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AccessibilityPage from './pages/AccessibilityPage';
import NotFoundPage from './pages/NotFoundPage';
import MetaLandingPage from './pages/landing/MetaLandingPage';
import LoginPage from './pages/auth/LoginPage';
import CreatorLoginPage from './pages/auth/CreatorLoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import RegistrationStepsPage from './pages/auth/RegistrationStepsPage';
import RegistrationCompletePage from './pages/auth/RegistrationCompletePage';

// Protected Pages
import DashboardPage from './pages/dashboard/DashboardPage';
import MyApplicationsPage from './pages/dashboard/MyApplicationsPage';
import ProfilePage from './pages/dashboard/ProfilePage';
import MessagesPage from './pages/dashboard/MessagesPage';

// Brand Pages
import MyCampaignsPage from './pages/dashboard/MyCampaignsPage';
import CreateCampaignPage from './pages/dashboard/CreateCampaignPage';
import EditCampaignPage from './pages/dashboard/EditCampaignPage';
import CampaignApplicationsPage from './pages/dashboard/CampaignApplicationsPage';

// Admin Pages
import {
  AdminCreatorsPage,
  AdminBrandsPage,
  AdminApplicationsPage,
  AdminPendingApplicationsPage,
  AdminCampaignsPage,
  AdminMessagesPage
} from './pages/admin';

// Initialize Meta Pixel globally & track page views on route changes
const MetaPixelTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    initMetaPixel();
  }, []);
  useEffect(() => {
    trackPageView();
  }, [pathname]);
  return null;
};

// Scroll to top on every route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children, roles, requireApproved = false }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-overlay">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Block unapproved creators from approved-only routes
  if (requireApproved && user.role === 'creator' && user.status !== 'approved') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Guest Route Component (redirect if logged in)
const GuestRoute = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-overlay">
        <div className="spinner" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <>
    <MetaPixelTracker />
    <ScrollToTop />
    <Routes>
      {/* Landing Pages — standalone, no header/footer */}
      <Route path="/lp/meta" element={<MetaLandingPage />} />

      {/* Public Routes */}
      <Route element={<MainLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/accessibility" element={<AccessibilityPage />} />
      </Route>

      {/* Auth Routes */}
      <Route
        path="/login"
        element={<LoginPage />}
      />
      <Route
        path="/forgot-password"
        element={<ForgotPasswordPage />}
      />
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
      />
      <Route
        path="/creator-login"
        element={
          <GuestRoute>
            <CreatorLoginPage />
          </GuestRoute>
        }
      />
      <Route
        path="/register"
        element={
          <GuestRoute>
            <RegisterPage />
          </GuestRoute>
        }
      />
      <Route
        path="/register/steps"
        element={
          <GuestRoute>
            <RegistrationStepsPage />
          </GuestRoute>
        }
      />
      <Route
        path="/register/complete"
        element={<RegistrationCompletePage />}
      />

      {/* Dashboard Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="messages" element={<MessagesPage />} />

        {/* Creator Routes - require approval */}
        <Route
          path="explore"
          element={
            <ProtectedRoute roles={['creator']} requireApproved>
              <CampaignsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="applications"
          element={
            <ProtectedRoute roles={['creator']} requireApproved>
              <MyApplicationsPage />
            </ProtectedRoute>
          }
        />

        {/* Brand Routes */}
        <Route
          path="campaigns"
          element={
            <ProtectedRoute roles={['brand', 'admin']}>
              <MyCampaignsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="campaigns/create"
          element={
            <ProtectedRoute roles={['brand', 'admin']}>
              <CreateCampaignPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="campaigns/:id/applications"
          element={
            <ProtectedRoute roles={['brand', 'admin']}>
              <CampaignApplicationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="campaigns/:id/edit"
          element={
            <ProtectedRoute roles={['brand', 'admin']}>
              <EditCampaignPage />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="admin/creators"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminCreatorsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/creators/pending"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminCreatorsPage filter="pending" />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/creators/approved"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminCreatorsPage filter="approved" />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/brands"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminBrandsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/applications"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminApplicationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/applications/pending"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminPendingApplicationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/campaigns"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminCampaignsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/messages"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminMessagesPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Admin redirect */}
      <Route
        path="/admin"
        element={<Navigate to="/dashboard/admin/creators/pending" replace />}
      />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    <AccessibilityWidget />
    <CookieConsent />
    </>
  );
}

export default App;
